/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-12
 */

// Add third-party dependencies.
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

// Add local dependencies.
import type { RootState } from '../../../store';
import { getSafeDisplayName } from '../../../utils/security';
import { useGetDashboardOverviewQuery } from '../../../features/dashboard/dashboardApi';
import { useGetUserStudiosQuery } from '../../../features/studios/studiosApi';
import { ARIA_ROLES } from '../../../utils/accessibility';
import Header from '../../layout/Header';
import LiveRegion from '../../common/LiveRegion';
import WelcomeCard from './DashboardWidgets/WelcomeCard';
import QuickActionsCard from './DashboardWidgets/QuickActionsCard';
import RecentActivityCard from './DashboardWidgets/RecentActivityCard';
import StatsCard from './DashboardWidgets/StatsCard';
import RecentStudioActivityCard from './DashboardWidgets/RecentStudioActivityCard';

/**
 * Dashboard component for authenticated users.
 * Provides an overview of studio tools and quick access to key features.
 *
 * @example
 * return (
 *   <Dashboard />
 * );
 *
 * @returns {React.ReactElement} The dashboard component
 */
const Dashboard: React.FC = () => {
  const { t } = useTranslation('dashboard');
  const user = useSelector((state: RootState) => state.auth.user);
  const displayName = getSafeDisplayName(user?.first_name, user?.email);

  // Fetch dashboard data from API
  const {
    data: dashboardData,
    isLoading: dashboardLoading,
    error: dashboardError,
  } = useGetDashboardOverviewQuery();

  // Fetch user's studios (same approach as Studios page)
  const {
    data: studiosData,
    isLoading: studiosLoading,
    error: studiosError,
  } = useGetUserStudiosQuery();

  const isLoading = dashboardLoading || studiosLoading;
  const error = dashboardError || studiosError;

  // Show loading state
  if (isLoading) {
    return (
      <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
        <Header />
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <LiveRegion message={t('loading.message')} politeness="POLITE" />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '50vh',
              gap: 2,
            }}
          >
            <CircularProgress size={50} />
            <Typography variant="h6" color="text.secondary">
              {t('loading.title')}
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
        <Header />
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <LiveRegion message={t('error.message')} politeness="ASSERTIVE" />
          <Alert severity="error" sx={{ mb: 4 }} role="alert">
            {t('error.failed')}
          </Alert>
          {/* Fallback to basic dashboard without API data */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              {t('title')}
            </Typography>
            <Typography variant="h6" color="text.secondary">
              {t('welcome.back', { name: displayName })}
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  // Use API data for display name if available
  const apiDisplayName = dashboardData?.data?.user_summary?.name
    ? dashboardData.data.user_summary.name
    : displayName;

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
      <Header />
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Live region for successful data load */}
        <LiveRegion
          message={t('aria.loadSuccess', { name: apiDisplayName })}
          politeness="POLITE"
        />

        {/* Dashboard Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {t('title')}
          </Typography>
          <Typography variant="h6" color="text.secondary">
            {t('welcome.back', { name: apiDisplayName })}
          </Typography>
        </Box>

        {/* Dashboard Layout */}
        <Box
          role={ARIA_ROLES.MAIN}
          aria-label={t('aria.content')}
          sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
        >
          {/* Top Row - Welcome and Quick Actions */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 3,
            }}
          >
            <WelcomeCard dashboardData={dashboardData} />
            <QuickActionsCard
              dashboardData={dashboardData}
              studiosData={studiosData}
            />
          </Box>

          {/* Stats Cards - Full width row */}
          <StatsCard dashboardData={dashboardData} />

          {/* Bottom Row - Recent Activity and Studio Management */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 3,
            }}
          >
            <RecentActivityCard dashboardData={dashboardData} />
            <RecentStudioActivityCard dashboardData={dashboardData} />
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Dashboard;
